// TaskDataRetention — Settings card for admin/super_admin/manager.
// Two capabilities:
//   1. Read-only auto-cleanup policy (server deletes tasks > 6 months old daily)
//   2. Manual month-wise purge with preview-before-delete safeguard
//
// Backend endpoints:
//   GET    /api/tasks/purge/policy
//   GET    /api/tasks/purge/preview?year=YYYY&month=MM[&business_id=xxx]
//   DELETE /api/tasks/purge?year=YYYY&month=MM[&business_id=xxx]

import { useEffect, useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2, Info, AlertTriangle, Eye, Loader2, ClipboardList } from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function TaskDataRetention({ currentUser }) {
  const role = currentUser?.role;
  const canAccess = role === "super_admin" || role === "admin" || role === "manager";
  const { businessId } = useBusiness();

  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [scopeAll, setScopeAll] = useState(false);           // super_admin can purge across all businesses
  const [policy, setPolicy] = useState(null);
  const [preview, setPreview] = useState(null);              // { count, by_status, window_start, window_end }
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!canAccess) return;
    fetch(`${BACKEND_URL}/api/tasks/purge/policy`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(setPolicy)
      .catch(() => {});
  }, [canAccess]);

  // Whenever the month/year/scope changes, the previous preview is stale
  useEffect(() => { setPreview(null); }, [year, month, scopeAll]);

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y, y - 1, y - 2, y - 3].map(String);
  }, [now]);

  if (!canAccess) return null;

  const buildQuery = () => {
    const q = new URLSearchParams({ year, month });
    if (!scopeAll && businessId) q.set("business_id", businessId);
    return q.toString();
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/tasks/purge/preview?${buildQuery()}`, { credentials: "include" });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Preview failed (HTTP ${r.status})`);
        return;
      }
      setPreview(await r.json());
    } catch (e) {
      toast.error("Preview failed: " + e.message);
    } finally { setLoadingPreview(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/tasks/purge?${buildQuery()}`, {
        method: "DELETE", credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Delete failed (HTTP ${r.status})`);
        return;
      }
      const result = await r.json();
      toast.success(`Deleted ${result.deleted} task${result.deleted === 1 ? "" : "s"} for ${MONTHS[Number(month) - 1]} ${year}`);
      setPreview(null);
      setConfirmOpen(false);
    } catch (e) {
      toast.error("Delete failed: " + e.message);
    } finally { setDeleting(false); }
  };

  return (
    <Card className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:col-span-2" data-testid="task-data-retention-card">
      <CardHeader>
        <CardTitle className="text-xl font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-rose-600" />
          Task Data Retention
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Auto-cleanup policy strip */}
        <div className="flex items-start gap-3 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-xl p-3">
          <Info className="w-4 h-4 text-indigo-600 dark:text-indigo-400 mt-0.5 shrink-0" />
          <div className="text-sm text-indigo-900 dark:text-indigo-100">
            <b>Auto-cleanup is on.</b> Tasks with no activity in the last{" "}
            <b>{policy?.auto_delete_months ?? 6} months</b> are removed daily by the server.
            Related comments, activities, and dependencies are removed with them.
          </div>
        </div>

        {/* Manual month-wise delete */}
        <div>
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">
            Manual purge — delete tasks by month
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-[140px]" data-testid="purge-month-select">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-[110px]" data-testid="purge-year-select">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>

            {role === "super_admin" && (
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 select-none ml-1">
                <input type="checkbox" checked={scopeAll} onChange={e => setScopeAll(e.target.checked)} className="w-4 h-4" />
                All businesses
              </label>
            )}

            <Button
              onClick={handlePreview}
              variant="outline"
              disabled={loadingPreview}
              data-testid="purge-preview-btn"
            >
              {loadingPreview ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}
              Preview
            </Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!preview || preview.count === 0 || deleting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="purge-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete {preview?.count ? `${preview.count} task${preview.count === 1 ? "" : "s"}` : "…"}
            </Button>
          </div>

          {preview && (
            <div className="mt-3 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
              <div className="text-sm text-slate-900 dark:text-slate-100">
                <b className="text-rose-600 dark:text-rose-400">{preview.count}</b> task{preview.count === 1 ? "" : "s"} match{preview.count === 1 ? "es" : ""} <b>{MONTHS[Number(month) - 1]} {year}</b>
                {!scopeAll && businessId && <> · scoped to current business</>}.
              </div>
              {preview.by_status && Object.keys(preview.by_status).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {Object.entries(preview.by_status).map(([s, n]) => (
                    <span key={s} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                      {s}: <b>{n}</b>
                    </span>
                  ))}
                </div>
              )}
              {preview.count === 0 && (
                <div className="text-xs text-slate-500 mt-1">Nothing to delete for this window.</div>
              )}
            </div>
          )}
        </div>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
                Permanently delete {preview?.count} task{preview?.count === 1 ? "" : "s"}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Tasks from <b>{MONTHS[Number(month) - 1]} {year}</b> and their comments, activities,
                and dependencies will be deleted permanently.
                {!scopeAll && businessId
                  ? " Scoped to the current business."
                  : " Across ALL businesses."}
                <br /><br />
                <span className="text-rose-700 dark:text-rose-400 font-semibold">This cannot be undone.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="purge-confirm-btn"
              >
                {deleting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Deleting…</> : <><Trash2 className="w-4 h-4 mr-1" /> Delete permanently</>}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
